import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import LocationSearch, { GeoPoint } from "./LocationSearch";
import { useIsMobile } from "../hooks/useIsMobile";

const CALENDAR_ICON_URL =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z' fill='%23374151'/%3E%3C/svg%3E";

interface Props {
  compact?: boolean;
  onToggleFilters?: () => void;
  filtersOpen?: boolean;
}

function locationFromParams(params: URLSearchParams): GeoPoint | null {
  const label = params.get("city");
  const lat = params.get("lat");
  const lng = params.get("lng");
  if (label && lat && lng) {
    const latN = parseFloat(lat);
    const lngN = parseFloat(lng);
    if (!isNaN(latN) && !isNaN(lngN)) return { label, lat: latN, lng: lngN };
  }
  return null;
}

export default function SearchBar({ compact, onToggleFilters, filtersOpen }: Props) {
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();

  const [pendingLocation, setPendingLocation] = useState<GeoPoint | null>(() => locationFromParams(params));
  const [pendingDate,     setPendingDate]     = useState<string>(() => params.get("date") ?? "");

  // Re-sync when URL changes externally (back/forward, FilterBar updates).
  useEffect(() => {
    setPendingLocation(locationFromParams(params));
    setPendingDate(params.get("date") ?? "");
  }, [params]);

  const handleSearch = () => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      if (pendingLocation) {
        next.set("city", pendingLocation.label);
        next.set("lat", String(pendingLocation.lat));
        next.set("lng", String(pendingLocation.lng));
      } else {
        next.delete("city");
        next.delete("lat");
        next.delete("lng");
        next.delete("radius");
      }
      if (pendingDate) next.set("date", pendingDate);
      else next.delete("date");
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  const todayLocal = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();

  return (
    <form onSubmit={handleSubmit} style={{ ...barStyle, padding: compact ? "10px 16px" : "16px 20px" }}>
      <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: isMobile ? "100%" : "auto", minWidth: 0 }}>
        <LocationSearch
          value={pendingLocation}
          onChange={setPendingLocation}
          placeholder="City / Township / Zip"
        />
      </div>

      <input
        type="date"
        className="icon-only-date"
        value={pendingDate}
        onChange={e => setPendingDate(e.target.value)}
        min={todayLocal}
        style={dateInputStyle}
        aria-label="Filter by date"
        title={pendingDate || "Any date"}
      />

      <button type="submit" style={searchBtnStyle}>
        Search
      </button>

      {onToggleFilters && (
        <button
          type="button"
          onClick={onToggleFilters}
          style={{ ...filterBtnStyle, background: filtersOpen ? "#2563eb" : "#fff", color: filtersOpen ? "#fff" : "#374151" }}
        >
          Filters {filtersOpen ? "▲" : "▼"}
        </button>
      )}
    </form>
  );
}

const barStyle: React.CSSProperties = {
  display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
};

const dateInputStyle: React.CSSProperties = {
  position: "relative",
  width: 40, height: 40, padding: 0, border: "1px solid #d1d5db",
  borderRadius: 6, color: "transparent", flexShrink: 0,
  colorScheme: "light", cursor: "pointer", WebkitAppearance: "none",
  backgroundColor: "#fff",
  backgroundImage: `url("${CALENDAR_ICON_URL}")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  backgroundSize: "18px 18px",
};

const searchBtnStyle: React.CSSProperties = {
  padding: "9px 22px", background: "#B24E10", color: "#fff",
  border: "none", borderRadius: 6, fontSize: 14, fontWeight: 600,
  cursor: "pointer", flexShrink: 0, boxShadow: "0 2px 6px rgba(178,78,16,0.35)",
};

const filterBtnStyle: React.CSSProperties = {
  padding: "9px 14px", border: "1px solid #d1d5db", borderRadius: 6,
  fontSize: 13, cursor: "pointer", flexShrink: 0, fontWeight: 500,
};
